import { UnprocessableFields } from 'api-clients/monolith/models/UnprocessableFields';
import { Warning as WarningIcon } from 'icons/fds';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { Typography } from '../../components/Typography';
import { StageContext } from '../../contexts/stageContext';
import { useStyles } from './styles';

export interface ServerFieldErrorProps {
  id: string;
}

// Hire judges some answers only when it saves the applicant, so its verdict
// arrives after the browser's own checks passed. The message stays under the
// question until the applicant edits the answer or submits again.
export const ServerFieldError: React.VFC<ServerFieldErrorProps> = ({ id }) => {
  const styles = useStyles();
  const { serverValidationErrors } = useContext(StageContext);
  // The error toast clears the submit error when it closes, after six seconds
  // or on any click, so the field keeps the last 422 until a new one replaces it.
  const [errors, setErrors] = useState(serverValidationErrors);
  if (serverValidationErrors && serverValidationErrors !== errors) {
    setErrors(serverValidationErrors);
  }
  const [dismissed, setDismissed] = useState<UnprocessableFields>();
  const ref = useRef<HTMLDivElement>(null);

  const messages = errors?.errors?.[id];
  const shown = !!messages && messages.length > 0 && dismissed !== errors;

  // The message renders beside the input rather than around it: consent and
  // focus code walk the DOM from an input to its parent, so a wrapper would
  // break them. An edit anywhere in the question bubbles to that parent.
  useEffect(() => {
    const container = ref.current?.parentElement;
    if (!shown || !container) return undefined;

    const dismiss = () => setDismissed(errors);
    container.addEventListener('input', dismiss);
    container.addEventListener('change', dismiss);
    return () => {
      container.removeEventListener('input', dismiss);
      container.removeEventListener('change', dismiss);
    };
  }, [shown, errors]);

  if (!shown || !messages) return null;

  return (
    <div ref={ref} className={styles.serverError}>
      <WarningIcon className={styles.serverErrorIcon} aria-hidden />
      <Typography variant="subtitle" color="red600" role="alert">
        {messages.join(' ')}
      </Typography>
    </div>
  );
};
